import React from 'react';
import {getRandomArrayElement} from '../../library';
import {getStoryIntervention} from '../../services/interventionSelection';
import {COPY_STRINGS} from '../../constants/translatedCopy';
import {endUserSession, willEndUserSessionStrand, getUserData} from '../../services/API';
import story_badge from "../../images/EndofAssessmentComp-img-02.svg";
import { getCopyByGradeband } from '../../services/ThemingService';
import { assignmentTypes } from '../../constants/constants';
import backIconSimple from '../../images/icons/iconBack_simple.svg';
import readAStorySimple from '../../images/icons/iconReadStory_simple.svg';
import classNames from 'classnames';
import { logSentryException } from '../../services/util';
import Loading from '../Global/Loading';

//Status Enums
const statusTypes = {
  IMPROVED: "improved",
  IMPROVED_WPM: "improved_wpm",
  IMPROVED_ACCURACY: "improved_accuracy",
  WORSE_BOTH: "worse_both",
  WORSE_WPM: "worse_wpm",
  WORSE_ACCURACY: "worse_accuracy",
  NO_CHANGE: "no_change",
};
export const studentStatusTypes = {
  STORY: "story",
  TIMED: "timed",
  GENERIC: "generic",
  ASSESSMENT: "assessment",
}

export class StudentStatus extends React.Component {
  constructor (props){
    super(props);
    this.state = {
      displayText: [],
      displayNavButtons: false,
      showCloseTabMessage: false,
      buttonsDisabled: false,
    };
    this.pointsPerStory = 7;
    this.speechTimeoutId = null;
    this.navigationInProgress = false;
    this._actionLocked = false;

    //Randomized Copy
    this.noHistoricData = getCopyByGradeband(COPY_STRINGS, 'no_historic_data_speech'); // No previous data for stat comparison
    this.onFire = getCopyByGradeband(COPY_STRINGS, 'onFire'); //Read Fast & Accurate
    this.goodAccuracy = getCopyByGradeband(COPY_STRINGS, 'goodAccuracy'); //Read Accurate
    this.workOn = getCopyByGradeband(COPY_STRINGS, 'workOn'); //Work Needed
    this.goodSpeed = COPY_STRINGS.goodSpeed; //Read Fast
    this.noImprovement = getCopyByGradeband(COPY_STRINGS, 'noImprovement'); // Status is some form of decrease or no change
    this.wrapUp = getCopyByGradeband(COPY_STRINGS, 'wrapUp'); //Work Needed

    this.openStoryPicker = this.openStoryPicker.bind(this);
    this.moveToInbox = this.moveToInbox.bind(this);
    this.handleButtonHover = this.handleButtonHover.bind(this);
  }

  componentDidMount(){

    let wpmDifference = this.props.currentMetrics.wcpm - this.props.historicMetrics.avgWPM;
    let accuracyDifference = this.props.currentMetrics.accuracy - this.props.historicMetrics.avgAccuracy;
    let totalPoints = this.props.historicMetrics.totalPointsAccrued + this.pointsPerStory;

    let statusType = this.getStatusType(wpmDifference, accuracyDifference);

    let displayText = this.getDisplayText(statusType, wpmDifference, accuracyDifference, totalPoints);
    let textToSpeakArray = this.getTextToSpeak(statusType, wpmDifference, accuracyDifference, totalPoints);
    
    // Enhanced speech completion handler with error handling
    const handleSpeechComplete = (error = null) => {
      if (this.speechTimeoutId) {
        clearTimeout(this.speechTimeoutId);
        this.speechTimeoutId = null;
      }
      
      if (error) {
        console.error('StudentStatus speech error:', error);
        logSentryException(new Error('StudentStatus speech sequence playback failure'), 'StudentStatus speech sequence failed', { originalError: error?.message, originalStack: error?.stack });
      }
      
      if (!this.navigationInProgress) {
        this.goHome();
      }
    };

    // Add timeout safeguard to ensure goHome is called
    this.speechTimeoutId = setTimeout(() => {
      console.warn('StudentStatus: Speech sequence timed out after 20 seconds, proceeding to goHome');
      logSentryException(new Error('Speech timeout'), 'StudentStatus speech sequence timeout', {
        textToSpeakLength: textToSpeakArray.length,
        statusType: statusType
      });
      handleSpeechComplete();
    }, 20000);

    let sequenceToSpeak = {
      textToSpeak: textToSpeakArray,
      passThroughSpeechTags: true,
      onEnd: () => handleSpeechComplete(),
      onError: (error) => handleSpeechComplete(error),
    }
    
    try {
      this.props.speech.speakSequence(sequenceToSpeak);
    } catch (error) {
      console.error('StudentStatus: Failed to start speech sequence:', error);
      logSentryException(new Error('StudentStatus speech sequence initialization failure'), 'StudentStatus speech sequence initialization failed', { originalError: error?.message, originalStack: error?.stack });
      // Immediate fallback if speech fails to start
      setTimeout(() => handleSpeechComplete(error), 5000);
    }
    
    this.setState({
      displayText: this.processDisplayText(displayText),
      // Surface the "close this tab" message as soon as the completion modal appears, rather than
      // after the completion speech ends (goHome runs on speech end).
      showCloseTabMessage: this.isStrandedCompletion(),
    });
  }

  // True when this completion has no way out: goHome would fall to its endUserSession branch
  // (no returnUrl, not redirecting home) and endUserSession would be unable to end the session
  // (no window.opener to close the tab and no trusted redirect) — so the student stays on this
  // screen and needs the "close this tab" message.
  isStrandedCompletion(){
    return !this.props.returnUrl && !this.props.redirect && willEndUserSessionStrand();
  }

  componentWillUnmount(){
    if (this.speechTimeoutId) {
      clearTimeout(this.speechTimeoutId);
      this.speechTimeoutId = null;
    }
    if (this._recoveryTimeoutId) {
      clearTimeout(this._recoveryTimeoutId);
      this._recoveryTimeoutId = null;
    }
  }

  canDisplayNavButtons(){
    const userData = getUserData();
    const { tutor } = userData.subscriptions || {};
    return window.location.pathname === '/student/tutor' && tutor && !this.props.hasStudentAssignment;
  }

  goHome(){
    // Prevent duplicate calls
    if (this.navigationInProgress) {
      console.log('StudentStatus: goHome already in progress, skipping duplicate call');
      return;
    }

    this.navigationInProgress = true;

    // Clear any pending speech timeout
    if (this.speechTimeoutId) {
      clearTimeout(this.speechTimeoutId);
      this.speechTimeoutId = null;
    }

    // If this.props.returnUrl exists, then navigate to that URL
    if (this.props.returnUrl) {
      // If student is a tutor subscriber and not an instruct subscriber, we give option to read another story / go back to inbox.
      if (this.canDisplayNavButtons()) {
        this.setState({ displayNavButtons: true });
        return;
      }
      window.location.href = this.props.returnUrl;
      return;
    }

    if(this.props.redirect){
      this.props.history.push('/student/home');
    } else {
      // Non-inbox completion (e.g. HMH launcher): try to close the tab / redirect. When neither is
      // possible the student is stranded here; componentDidMount has already surfaced the "close
      // this tab" message for that case.
      window.setTimeout(() => endUserSession(), 3000);
    }
  }

  getStatusType(wpmDifference, accuracyDifference){
    if(wpmDifference > 0 && accuracyDifference > 0){
      return statusTypes.IMPROVED;
    } else if (wpmDifference > 0){
      return statusTypes.IMPROVED_WPM;
    } else if(accuracyDifference > 0){
      return statusTypes.IMPROVED_ACCURACY;
    } else if(wpmDifference < 0 && accuracyDifference < 0){
      return statusTypes.WORSE_BOTH;
    } else if(wpmDifference < 0){
      return statusTypes.WORSE_WPM;
    } else if(accuracyDifference < 0){
      return statusTypes.WORSE_ACCURACY;
    } else {
      return statusTypes.NO_CHANGE;
    }
  }

  // wrap each piece of text in a div for easier styling
  processDisplayText(textArray){
    return textArray.map((phrase, index) => {
      return (<div className="phrase" key={"phrase" + index}>{phrase}</div>);
    });
  }

  // return different display text for each status case. Although this has some duplication
  // (text which will be the same in all cases which has not been abstracted), this is far more readable than the more abstracted way
  // additionally, this will allow the cases to diverge further in the future without pain
  getDisplayText(statusType, wpmDifference, accuracyDifference, totalPoints){
    const ecouragement = COPY_STRINGS.keep_up;
    let frontBookEnd = COPY_STRINGS.status_stats_speech_front(this.props.currentMetrics.wcpm, this.props.currentMetrics.accuracy);

    switch(statusType){
      case statusTypes.IMPROVED:
        return [
          frontBookEnd,
          COPY_STRINGS.stats_compare_improved(wpmDifference, accuracyDifference),
        ];
        break;
      case statusTypes.IMPROVED_WPM:
        return [
          frontBookEnd,
          COPY_STRINGS.stats_compare_improved_wpm(wpmDifference),
        ];
        break;
      case statusTypes.IMPROVED_ACCURACY:
        return [
          frontBookEnd,
          COPY_STRINGS.stats_compare_improved_accuracy(accuracyDifference),
        ];
        break;
      case statusTypes.WORSE_WPM:
        return [
          frontBookEnd
         // ecouragement
        ];
        break;
      case statusTypes.WORSE_ACCURACY:
        return [
          frontBookEnd
         // ecouragement
        ];
        break;
      case statusTypes.WORSE_BOTH:
        return [
          frontBookEnd
          //ecouragement
        ];
        break;
      case statusTypes.NO_CHANGE:
        return [
          frontBookEnd
         // ecouragement
        ];
        break;
      default:
        return [
          frontBookEnd
          //ecouragement
        ];
        break;
    }
  }

  // return different display text for each status case. Although this has some duplication
  // (text which will be the same in all cases which has not been abstracted), this is far more readable than the more abstracted way
  // additionally, this will allow the cases to diverge further in the future without pain
  getTextToSpeak(statusType, wpmDifference, accuracyDifference, totalPoints){
    let textToSpeakArray = [];

    // if we're not showing stats, simplify speech as well
    if(!this.props.showStats){
      switch(this.props.type){
        case studentStatusTypes.TIMED:
          textToSpeakArray.push({textToSpeak: COPY_STRINGS.times_up_speech});
          break;
        case studentStatusTypes.STORY:
          textToSpeakArray.push({textToSpeak: getCopyByGradeband(COPY_STRINGS, 'finished_story_speech')});
          break;
        case studentStatusTypes.ASSESSMENT:
          textToSpeakArray.push({textToSpeak: COPY_STRINGS.finished_assessment_speech});
          break;
        default:
          textToSpeakArray.push({textToSpeak: COPY_STRINGS.all_done_speech});
          break;
      }
      return textToSpeakArray;
    }

    let backBookEnd = getRandomArrayElement(this.wrapUp);

    // if there is no historic data, leave out the comparisons
    if(this.props.historicMetrics.avgWPM <= 0 || this.props.historicMetrics.avgAccuracy <= 0){
      textToSpeakArray.push({textToSpeak: getRandomArrayElement(this.noHistoricData)})
      textToSpeakArray.push({textToSpeak: backBookEnd})
      return textToSpeakArray;
    }

    switch(statusType){
      case statusTypes.IMPROVED:
        textToSpeakArray.push({textToSpeak: getCopyByGradeband(COPY_STRINGS, 'stats_speech_compare_improved_both_one')})
        textToSpeakArray.push({textToSpeak: COPY_STRINGS.stats_speech_compare_improved_both_two})
        textToSpeakArray.push({textToSpeak: getRandomArrayElement(this.onFire)})
        break;
      case statusTypes.IMPROVED_WPM:
        textToSpeakArray.push({textToSpeak: getCopyByGradeband(COPY_STRINGS, 'stats_speech_compare_improved_wpm_one')})
        textToSpeakArray.push({textToSpeak: getRandomArrayElement(this.goodSpeed)})
        break;
      case statusTypes.IMPROVED_ACCURACY:
        textToSpeakArray.push({textToSpeak: COPY_STRINGS.stats_speech_compare_improved_accuracy_one})
        textToSpeakArray.push({textToSpeak: getRandomArrayElement(this.goodAccuracy)})
        break;
      case statusTypes.WORSE_WPM:
      case statusTypes.WORSE_ACCURACY:
      case statusTypes.WORSE_BOTH:
      case statusTypes.NO_CHANGE:
        textToSpeakArray.push({textToSpeak: getRandomArrayElement(this.noImprovement)})
        break;
      default:
        break;
    }

    textToSpeakArray.push({textToSpeak: backBookEnd})

    return textToSpeakArray;

  }

  getAlertText(){
    if(!this.props.showStats){
      switch(this.props.type){
        case studentStatusTypes.TIMED:
          return COPY_STRINGS.times_up;
        case studentStatusTypes.STORY:
          return COPY_STRINGS.finished_story;
        default:
          return COPY_STRINGS.all_done;
      }
    }
  }

  moveToInbox() {
    if (this._actionLocked) return;
    this._actionLocked = true;
    this.setState({ buttonsDisabled: true });
    this.props.speech.abortSpeech();
    this._startRecoveryTimeout();
    window.location.href = this.props.returnUrl;
  }

  openStoryPicker() {
    if (this._actionLocked) return;
    this._actionLocked = true;
    this.setState({ buttonsDisabled: true });
    this.props.speech.abortSpeech();
    this._startRecoveryTimeout();
    this.props.resetStory();
  }

  handleButtonHover(textToSpeak) {
    if (this._actionLocked) return;
    this.props.speech.abortSpeech(() => {
      this.props.speech.speak({
        textToSpeak: textToSpeak
      });
    });
  }

  /**
   * Safety net: if the component is still mounted and locked after 15s
   * (e.g. fetch hang or abortSpeech callback drop), restore buttons so
   * the student can retry or navigate away.
   */
  _startRecoveryTimeout() {
    clearTimeout(this._recoveryTimeoutId);
    this._recoveryTimeoutId = setTimeout(() => {
      if (this._actionLocked) {
        this._actionLocked = false;
        this.setState({ buttonsDisabled: false });
      }
    }, 15000);
  }

  render() {
    let stats = this.props.showStats ? (
      <div className="stats">
        <div className="statContainer left">
          <div className="stat">{this.props.currentMetrics.wcpm}</div>
          <div className="statDescription">{COPY_STRINGS.speed}</div>
        </div>
        <div className="statContainer right">
          <div className="stat">{this.props.currentMetrics.accuracy + "%"}</div>
          <div className="statDescription">{COPY_STRINGS.correct}</div>
        </div>
      </div>
    ) : null;

    let btnOptions = (
      <div className="optionsWrapper">
        <button 
          className="optionButton"
          onClick={this.moveToInbox}
          onMouseEnter={() => this.handleButtonHover(COPY_STRINGS.finished)}
        >
          <img src={backIconSimple} alt="back_icon"/>
          <span className="optionTitle">{COPY_STRINGS.finished}</span>
        </button>
        <>
          <button 
            className="optionButton readMoreBtn"
            onClick={this.openStoryPicker}
            onMouseEnter={() => this.handleButtonHover(COPY_STRINGS.read_more)}
          >
            <img src={readAStorySimple} alt="read_more_icon"/>
            <span className="optionTitle">{COPY_STRINGS.read_more}</span>
          </button>
        </>
      </div>
    )

    return (
      <div className={classNames("endOfStoryModal", {
        "noMaxWidth": this.canDisplayNavButtons()
      })}>
        <div className="alert">{this.getAlertText()}</div>
        <div className="praise">{COPY_STRINGS.great_work}</div>
        <div className="badgeContainer">
          <img className="badge" src={story_badge} alt="" />
        </div>
        {this.state.showCloseTabMessage ? (
          <div className="closeTabMessage" role="status" aria-live="polite">{COPY_STRINGS.please_close_tab}</div>
        ) : null}
        {stats}
        {this.state.displayNavButtons ? (
          this.state.buttonsDisabled
            ? <div className="endOfStoryLoading"><Loading noFill={true}/></div>
            : btnOptions
        ) : null}
      </div>
    );
  }
}
